/**
 * Colorize Tasks — a muted blurred-ellipse glow behind a task row, gated by three
 * independent Display settings (`settings.ts`'s `colorizeEnergy`/`colorizeOverdue`/
 * `colorizeImportant`, all off by default). Purely decorative: Energy/Overdue/Important
 * are already shown as explicit badges wherever a row renders (`TaskList.tsx`,
 * `RecurringCard.tsx`), so this adds no new information — just visual distinctness
 * between rows in a list.
 *
 * Rendered as the first child inside `.task`, ahead of the tick/title/actions — as a flex
 * item with no explicit z-index, first-DOM-order paints it behind its siblings. Its own
 * `overflow: hidden` (styles.css) is what clips the blur to the row's rounded rect; `.task`
 * itself deliberately stays unclipped so the Execute-mode kebab dropdown can still overflow
 * its row (see the comment on `.task:focus-within` in styles.css).
 */

import { ENERGY_COLORS } from "./energyPips";
import { useSettings } from "../settings";

export function TaskGlow({
  energy,
  overdue,
  important,
}: {
  energy: number | null;
  overdue: boolean;
  important: boolean;
}) {
  const settings = useSettings();

  const showEnergy = settings.colorizeEnergy && energy !== null;
  const showOverdue = settings.colorizeOverdue && overdue;
  const showImportant = settings.colorizeImportant && important;

  if (!showEnergy && !showOverdue && !showImportant) return null;

  return (
    <span className="task-glow" aria-hidden="true">
      {showEnergy && (
        <span
          className="task-glow-dot energy"
          style={{ background: ENERGY_COLORS[energy as number] }}
        />
      )}
      {showOverdue && <span className="task-glow-dot overdue" />}
      {showImportant && <span className="task-glow-dot important" />}
    </span>
  );
}
